import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { randomId } from "@excalidraw/common";

import { useState, useCallback, useRef } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { VoiceInputButton } from "./VoiceInputButton";
import { AttachImageThumbnails, AttachImageButton } from "./AttachImage";

import { getBackendUrl } from "../utils/backendUrl";
import "./AICanvasInput.scss";

// Icons
const SparklesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
    <path d="M5 3v4" />
    <path d="M19 17v4" />
    <path d="M3 5h4" />
    <path d="M17 19h4" />
  </svg>
);

const SendIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m22 2-7 20-4-9-9-4Z" />
    <path d="M22 2 11 13" />
  </svg>
);

interface Operation {
  type: "modify" | "add" | "delete";
  elementId?: string;
  changes?: Record<string, unknown>;
  element?: Record<string, unknown>;
}

interface CanvasEditResponse {
  operations: Operation[];
  error?: string;
  action?: "edit" | "regenerate";
  regenerate_prompt?: string;
}

interface AICanvasInputProps {
  excalidrawAPI: ExcalidrawImperativeAPI;
}

export const AICanvasInput = ({ excalidrawAPI }: AICanvasInputProps) => {
  const [prompt, setPrompt] = useState("");
  const [pendingImageUrls, setPendingImageUrls] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipNextEnterRef = useRef(false);

  const executeOperations = useCallback(
    (operations: Operation[]) => {
      const currentElements = excalidrawAPI.getSceneElements();
      // Use any[] to avoid complex type issues with Ordered<> types
      const newElements: any[] = [...currentElements];
      let hasChanges = false;

      for (const op of operations) {
        if (op.type === "modify" && op.elementId && op.changes) {
          // Modify existing element
          const index = newElements.findIndex((el) => el.id === op.elementId);
          if (index !== -1) {
            const element = newElements[index];

            // FIX: Sync originalText when text is modified for text elements
            // This ensures double-click edit mode shows the updated text
            const changes = { ...op.changes };
            const hasTextChange = "text" in changes;
            const isTextElement = element.type === "text";
            const isBoundText = !!(element as any).containerId;
            if (hasTextChange && isTextElement) {
              // Sync originalText with text to prevent revert on double-click
              (changes as any).originalText = changes.text;
              // Enable autoResize so width adjusts to new text
              (changes as any).autoResize = true;

              // For bound text (text inside containers), we need to unbind it
              // Otherwise Excalidraw will re-wrap the text based on container width
              if (isBoundText) {
                const containerId = (element as any).containerId;
                // Unbind the text from container
                (changes as any).containerId = null;

                // Calculate new width based on text length and font size
                const newText = changes.text as string;
                const fontSize = (element as any).fontSize || 20;
                const estimatedWidth = Math.max(
                  newText.length * fontSize * 0.6,
                  100,
                );
                (changes as any).width = estimatedWidth;

                // Also update the container to remove the bound text reference
                const containerIndex = newElements.findIndex(
                  (el) => el.id === containerId,
                );
                if (containerIndex !== -1) {
                  const container = newElements[containerIndex];
                  const boundElements = (container.boundElements || []).filter(
                    (be: any) => be.id !== element.id,
                  );
                  newElements[containerIndex] = {
                    ...container,
                    boundElements:
                      boundElements.length > 0 ? boundElements : null,
                    version: container.version + 1,
                    versionNonce: Math.floor(Math.random() * 1000000000),
                    updated: Date.now(),
                  };
                }
              }
            }

            newElements[index] = {
              ...element,
              ...changes,
              version: element.version + 1,
              versionNonce: Math.floor(Math.random() * 1000000000),
              updated: Date.now(),
            };
            hasChanges = true;
          }
        } else if (op.type === "add" && op.element) {
          // Add new element
          const newElement = {
            ...op.element,
            id: (op.element.id as string) || randomId(),
            version: 1,
            versionNonce: Math.floor(Math.random() * 1000000000),
            isDeleted: false,
            updated: Date.now(),
            seed: Math.floor(Math.random() * 1000000000),
            groupIds: [],
            frameId: null,
            boundElements: null,
            link: null,
            locked: false,
            index: null, // Will be fixed by syncInvalidIndices in updateScene
            // Default values for missing properties
            angle: op.element.angle ?? 0,
            strokeColor: op.element.strokeColor ?? "#1e1e1e",
            backgroundColor: op.element.backgroundColor ?? "transparent",
            fillStyle: op.element.fillStyle ?? "solid",
            strokeWidth: op.element.strokeWidth ?? 2,
            strokeStyle: op.element.strokeStyle ?? "solid",
            roughness: op.element.roughness ?? 1,
            opacity: op.element.opacity ?? 100,
            roundness: op.element.roundness ?? null,
          };
          newElements.push(newElement);
          hasChanges = true;
        } else if (op.type === "delete" && op.elementId) {
          // Mark element as deleted
          const index = newElements.findIndex((el) => el.id === op.elementId);
          if (index !== -1) {
            const element = newElements[index];
            newElements[index] = {
              ...element,
              isDeleted: true,
              version: element.version + 1,
              versionNonce: Math.floor(Math.random() * 1000000000),
              updated: Date.now(),
            };
            hasChanges = true;
          }
        }
      }

      if (hasChanges) {
        excalidrawAPI.updateScene({
          elements: newElements,
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });
      }

      return hasChanges;
    },
    [excalidrawAPI],
  );

  const handleSubmit = useCallback(async () => {
    if (!prompt.trim() || isLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get current canvas state
      const elements = excalidrawAPI.getSceneElements();
      const appState = excalidrawAPI.getAppState();
      const selectedIds = Object.keys(appState.selectedElementIds);

      // Prepare elements data (only send necessary fields)
      const elementsData = elements
        .filter((el) => !el.isDeleted)
        .map((el) => {
          const base = {
            id: el.id,
            type: el.type,
            x: el.x,
            y: el.y,
            width: el.width,
            height: el.height,
            strokeColor: el.strokeColor,
            backgroundColor: el.backgroundColor,
            fillStyle: el.fillStyle,
            strokeWidth: el.strokeWidth,
            strokeStyle: el.strokeStyle,
            roughness: el.roughness,
            opacity: el.opacity,
            locked: el.locked,
          };
          // Include text content for text elements
          if ("text" in el) {
            return {
              ...base,
              text: el.text,
              fontSize: el.fontSize,
              fontFamily: el.fontFamily,
              textAlign: el.textAlign,
            };
          }
          return base;
        });

      // Call AI backend
      const response = await fetch(
        `${getBackendUrl()}/v1/ai/canvas/edit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            prompt: prompt.trim(),
            elements: elementsData,
            selectedIds,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: CanvasEditResponse = await response.json();

      if (data.action === "regenerate" && data.regenerate_prompt) {
        excalidrawAPI.updateScene({ elements: [] });
        setPrompt("");
        setPendingImageUrls([]);
        setError(null);
        excalidrawAPI.setToast({
          message:
            "Canvas cleared. Use the AI sidebar to generate a new diagram from your prompt.",
          duration: 4000,
        });
        return;
      }

      if (data.error) {
        setError(data.error);
        return;
      }

      if (data.operations.length === 0) {
        setError("AI could not understand the request");
        return;
      }

      // Execute operations
      const success = executeOperations(data.operations);
      if (success) {
        setPrompt("");
        setPendingImageUrls([]);
        excalidrawAPI.setToast({
          message: `Applied ${data.operations.length} operation(s)`,
          duration: 2000,
        });
      }
    } catch (err) {
      console.error("AI Canvas Edit error:", err);
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setIsLoading(false);
    }
  }, [prompt, isLoading, excalidrawAPI, executeOperations]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        if (e.nativeEvent.isComposing || skipNextEnterRef.current) {
          skipNextEnterRef.current = false;
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        handleSubmit();
      }
      e.stopPropagation();
    },
    [handleSubmit],
  );

  const handleCompositionEnd = useCallback(() => {
    skipNextEnterRef.current = true;
    setTimeout(() => {
      skipNextEnterRef.current = false;
    }, 100);
  }, []);

  const handleVoiceTranscript = useCallback((text: string) => {
    // Append transcribed text to existing prompt
    setPrompt((prev) => (prev ? `${prev} ${text}` : text));
    setError(null);
    // Focus input after transcription
    inputRef.current?.focus();
  }, []);

  const classNames = [
    "ai-canvas-input",
    isLoading ? "ai-canvas-input--loading" : "",
    error ? "ai-canvas-input--error" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classNames}>
      {error && <div className="ai-canvas-input__error">{error}</div>}
      <AttachImageThumbnails
        urls={pendingImageUrls}
        onRemove={(i) =>
          setPendingImageUrls((prev) => prev.filter((_, j) => j !== i))
        }
        className="ai-canvas-input__thumbnails"
      />
      <div className="ai-canvas-input__icon">
        <SparklesIcon />
      </div>
      <input
        ref={inputRef}
        type="text"
        className="ai-canvas-input__input"
        placeholder="Ask AI to modify canvas... (e.g., 'make selected elements blue')"
        value={prompt}
        onChange={(e) => {
          setPrompt(e.target.value);
          setError(null);
        }}
        onKeyDown={handleKeyDown}
        onCompositionEnd={handleCompositionEnd}
        disabled={isLoading}
      />
      <div className="ai-canvas-input__actions">
        <VoiceInputButton
          onTranscript={handleVoiceTranscript}
          disabled={isLoading}
          variant="default"
          layout="reverse"
          className="ai-canvas-input__voice-btn"
        />
        <AttachImageButton
          onAdd={(urls) => setPendingImageUrls((prev) => [...prev, ...urls])}
          disabled={isLoading}
          buttonClassName="ai-canvas-input__image-btn"
          title="Add image"
        />
        <button
          className="ai-canvas-input__send-btn"
          onClick={handleSubmit}
          disabled={isLoading || !prompt.trim()}
          title="Send (Enter)"
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
};
